import React, { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAxiosPrivate } from "@hooks/useAxiosPrivate";
import { logOut } from "@store/userReducer";

import LanguageSwitcher from "@components/LanguageSwitcher/LanguageSwitcher";
import UserProfileIcon from "@assets/svgs/UserProfileSvg";
import { HiOutlineUserCircle, HiArrowRightOnRectangle } from "react-icons/hi2";

/**
 * Hook that provides user menu state and dropdown data.
 * Used by UserMenu in header.
 */
export function useUserMenuData() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const axios = useAxiosPrivate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post("/auth/logout", {}, { withCredentials: true });
      dispatch(logOut());
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      dispatch(logOut());
      navigate("/login", { replace: true });
    } finally {
      setIsMenuOpen(false);
    }
  }, [axios, dispatch, navigate]);

  const handleMenuToggle = useCallback((open) => {
    setIsLanguageOpen(false);
    setIsMenuOpen(typeof open === "boolean" ? open : (prev) => !prev);
  }, []);

  const handleMenuItemClick = useCallback((item) => {
    if (item && !React.isValidElement(item.title)) {
      setIsMenuOpen(false);
      setIsLanguageOpen(false);
    }
  }, []);

  const menuData = useMemo(() => {
    const username =
      user?.userProfile?.username || user?.email || t("user", "User");
    const imageUrl = user?.userProfile?.image;
    const hasValidImage =
      typeof imageUrl === "string" &&
      imageUrl.trim().length > 0 &&
      (imageUrl.startsWith("http") || imageUrl.startsWith("data:"));
    const userImageUrl = hasValidImage ? imageUrl : <UserProfileIcon />;

    const menuItems = [
      {
        id: 1,
        title: "",
        list: [
          {
            id: "account-menu-profile",
            title: t("menu.userProfile", "My profile"),
            onClick: () => navigate("/user-account"),
            svg: <HiOutlineUserCircle aria-hidden="true" />,
          },
        ],
      },
      {
        id: 2,
        title: "",
        list: [
          {
            id: "account-menu-language",
            title: (
              <LanguageSwitcher
                type="header"
                inlineOptions
                isOpen2={isLanguageOpen}
                setIsOpen2={setIsLanguageOpen}
              />
            ),
          },
        ],
      },
      {
        id: 3,
        title: "",
        list: [
          {
            id: "account-menu-sign-out",
            title: t("menu.logout"),
            onClick: handleLogout,
            svg: <HiArrowRightOnRectangle aria-hidden="true" />,
          },
        ],
      },
    ];

    return {
      username,
      userImageUrl,
      menuItems,
    };
  }, [
    user?.email,
    user?.userProfile?.username,
    user?.userProfile?.image,
    isLanguageOpen,
    t,
    navigate,
    handleLogout,
  ]);

  return {
    ...menuData,
    isMenuOpen,
    handleMenuToggle,
    handleMenuItemClick,
  };
}
